// <Badge> — count or dot badge.
//
// Per ICON_SYSTEM_PLAYBOOK.md §"Icon Composition Rules": "A trailing badge
// with count is allowed for queue or list items." Counts > 99 render as `99+`.

import type { ReactNode } from 'react';
import { tokens } from '../tokens';

export interface BadgeProps {
  /** Numeric badge (clamped to 99 max display; > 99 renders `99+`). */
  count?: number;
  /** Text badge (e.g. "AI-generated"). */
  label?: string;
  /** When true, render as a dot (no number). */
  dot?: boolean;
  variant?: 'neutral' | 'info' | 'success' | 'warning' | 'error' | 'tertiary';
  /** When given, badge anchors to a child via relative positioning. */
  children?: ReactNode;
  /** Required when badge is icon/dot-only and conveys meaning. */
  'aria-label'?: string;
  className?: string;
  'data-testid'?: string;
}

function display(count: number | undefined): string {
  if (count == null) return '';
  if (count > 99) return '99+';
  return String(count);
}

export function Badge({
  count,
  label,
  dot = false,
  variant = 'neutral',
  children,
  'aria-label': ariaLabel,
  className,
  'data-testid': testId,
}: BadgeProps) {
  const bg =
    variant === 'info'
      ? tokens.color.semantic.info
      : variant === 'success'
        ? tokens.color.semantic.success
        : variant === 'warning'
          ? tokens.color.semantic.warning
          : variant === 'error'
            ? tokens.color.semantic.danger
            : variant === 'tertiary'
              ? tokens.color.accent.subtle
              : tokens.color.surface[3];
  const fg =
    variant === 'info' || variant === 'success' || variant === 'warning'
      ? tokens.color.ink.inverse
      : tokens.color.ink.primary;

  const badgeNode = (
    <span
      role={ariaLabel || label ? 'status' : undefined}
      aria-label={ariaLabel ?? label ?? (count != null ? `${count} unread` : undefined)}
      data-testid={testId}
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: bg,
        color: fg,
        borderRadius: tokens.radius.pill,
        padding: dot ? 0 : `0 ${tokens.space[1]}px`,
        minWidth: dot ? tokens.space[2] : tokens.space[3],
        height: dot ? tokens.space[2] : tokens.space[3],
        fontFamily: tokens.font.family.ui,
        fontSize: tokens.font.role.engMeta.size,
        fontWeight: tokens.font.weight.medium,
        lineHeight: 1,
      }}
    >
      {dot ? null : (label ?? display(count))}
    </span>
  );

  if (!children) return badgeNode;

  // Anchored: position absolute on the right-top corner of the child.
  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      {children}
      <span
        style={{
          position: 'absolute',
          top: -tokens.space[1],
          right: -tokens.space[1],
          pointerEvents: 'none',
        }}
      >
        {badgeNode}
      </span>
    </span>
  );
}
